import React, { useState } from "react";
import { ArrowRight, Clock, Cpu, Wallet } from "lucide-react";

const H100_COST = 25000;
const GPU_HOURS_PER_MONTH = 720 * 0.85;
const LOW = 0.15;
const HIGH = 0.3;
const PRESETS = [10000, 25000, 50000, 100000];
const MIN = 5000;
const MAX = 150000;

const fmt = (n) => Math.round(n).toLocaleString("fr-FR").replace(/\u202f|\u00a0/g, " ");

const Stat = ({ icon: I, label, value, testId, accent }) => (
  <div className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-4 text-left" data-testid={testId}>
    <div className="flex items-center gap-2 text-[10.5px] tracking-[0.18em] uppercase text-zinc-500">
      <I className="w-3.5 h-3.5" /> {label}
    </div>
    <p className={`mt-2 text-[22px] md:text-[26px] font-medium tracking-tight ${accent ? "text-emerald-400" : "text-zinc-100"}`}>{value}</p>
  </div>
);

export const GpuCalculator = ({ onCta }) => {
  const [value, setValue] = useState(25000);
  const gpus = value / H100_COST;
  const hours = gpus * GPU_HOURS_PER_MONTH;
  const pct = ((value - MIN) / (MAX - MIN)) * 100;

  return (
    <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-950/80 p-6 md:p-8 text-left" data-testid="gpu-calc-widget">
      <p className="text-[11px] tracking-[0.2em] uppercase text-zinc-500">Puissance mise à disposition</p>
      <p className="mt-2 text-[38px] md:text-[44px] font-medium tracking-tight" data-testid="gpu-calc-value">{fmt(value)} $</p>
      <input
        type="range" min={MIN} max={MAX} step={1000} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        data-testid="gpu-calc-range"
        className="mt-4 w-full h-1.5 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-400 [&::-moz-range-thumb]:w-6 [&::-moz-range-thumb]:h-6 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-emerald-400"
        style={{ background: `linear-gradient(to right, rgb(52 211 153) ${pct}%, rgb(39 39 42) ${pct}%)` }}
      />
      <div className="mt-4 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p} type="button" onClick={() => setValue(p)} data-testid={`gpu-calc-preset-${p}`}
            className={`px-4 py-1.5 rounded-full text-[12px] border transition-colors ${value === p ? "bg-emerald-500 border-emerald-500 text-zinc-950 font-semibold" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}
          >
            {p / 1000}k $
          </button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Stat icon={Cpu} label="GPU équivalents" value={`≈ ${gpus.toFixed(1).replace(".", ",")}`} testId="gpu-calc-gpus" />
        <Stat icon={Clock} label="Heures GPU / mois" value={fmt(hours)} testId="gpu-calc-hours" />
      </div>
      <div className="mt-3">
        <Stat icon={Wallet} label="Revenus potentiels / an" value={`${fmt(value * LOW)} $ – ${fmt(value * HIGH)} $`} testId="gpu-calc-revenue" accent />
      </div>
      <p className="mt-3 text-[12px] text-zinc-500" data-testid="gpu-calc-monthly">
        Soit environ {fmt((value * LOW) / 12)} $ à {fmt((value * HIGH) / 12)} $ par mois.
      </p>
      <button
        type="button" onClick={onCta} data-testid="gpu-calc-cta"
        className="mt-6 w-full h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[13px] tracking-[0.12em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
      >
        Être rappelé <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};
